import React, { useEffect, useRef, useLayoutEffect, useCallback, useState, useMemo } from 'react';
import { Button } from '@guildeducationinc/recess';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { InputTag } from '../InputTag';
import { useResizeWidthObserver } from '../../../hooks/useResizeWidthObserver';
import styles from './InputTagCollapsingList.module.scss';

// The +2 button that shows up when the list is collapsed
const SEE_MORE_BUTTON_WIDTH = 56;

// The 'Clear all' button that is always present in the list
const CLEAR_ALL_BUTTON_WIDTH = 75;

// How much to delay before calculating new item widths on resize
const debounceTime = 50;

// When calculating how many input tags to render, we want to ignore the clear all
// and see more buttons so they don't get included in the list of indices to ignore
// There widths are already accounted for when calculating overflow widths
const childIdsToIgnoreFromMeasurement = ['show-collapse-button', 'clear-all-button'];
const excludeNonTagsFromMeasurement = (child: Element) =>
	childIdsToIgnoreFromMeasurement.indexOf(child.id) === -1;

export interface BaseInputTagListProps<T> {
	items: { value: T; label: string }[];
}

export interface EdittableTagListProps<T> extends BaseInputTagListProps<T> {
	/**
	 * This function will get the tag that the user clicked on as a parameter
	 * allowing the parent component to handle the click (eg: managing state)
	 */
	removeFunction: (item) => void;

	/**
	 * The function to call when the user clicks the clear all button
	 */
	removeAll: () => void;
}

type InputTagListProps<T> = BaseInputTagListProps<T> | EdittableTagListProps<T>;

export const InputTagCollapsingList = <T extends Record<string, unknown>>(
	props: InputTagListProps<T>
) => {
	const { items } = props;
	const isEdittableInputingTagList = 'removeFunction' in props;
	const removeAll = ('removeAll' in props && props.removeAll) || undefined;
	const removeFunction = ('removeFunction' in props && props.removeFunction) || undefined;
	// use this to measure the container rendered off the page that will be used to render
	// ALL the input tags and determine based on children width and parent width, at what
	// point the list should collapse
	const measuringContainerRef = useRef<HTMLDivElement>(null);
	// use this to measure the width of the parent container that will house
	// the InputTagList so we know what width to give the measuringContainer
	// (since the measuringContainer is absolutely positioned off the page,
	// we need to give it the same width that the actual InputTagList will be fitting into)
	const mainTagWrapperRef = useRef<HTMLDivElement>(null);

	// toggle whether the list is collapsed or not
	const [showAllMode, setShowAllMode] = useState<boolean>(false);

	// the indices of tags that won't show but will instead collapse into the see more button
	const [hiddenNavIndexes, setHiddenNavIndexes] = useState<number[]>([]);

	// A width observer that will trigger whenever the width of the input tag container changes
	const observedWidth = useResizeWidthObserver(mainTagWrapperRef);

	/**
	 * calculate nav will render ALL the input tags from props in a parallel container rendered
	 * off the page (see the `measure` style in the stylesheet) and then calculate the width of all
	 * the tags and determine which tags should be collapsed into the see more button by counting how
	 * many tags fit into the parent container (represented by the mainTagWrapperRef).
	 */

	// The Clear all button will always be there if we have an edittable list, otherwise math should act
	// as if it is not there
	const clearAllButtonWidth = isEdittableInputingTagList ? CLEAR_ALL_BUTTON_WIDTH : 0;

	const calculateNav = useCallback(() => {
		if (measuringContainerRef.current) {
			const parentWidth = measuringContainerRef.current.clientWidth;
			const children = Array.from(measuringContainerRef.current.children);

			if (parentWidth > 0 && children.length > 1) {
				let totalWidth = clearAllButtonWidth;

				// figure out what index starts to overflow with JUST tag input children (exclude see more button)
				const overflowedIndex = children.filter(excludeNonTagsFromMeasurement).findIndex(child => {
					// width of the input tag + 8px for 4px margin each side
					totalWidth = totalWidth + child.clientWidth + 8;
					return totalWidth >= parentWidth;
				});

				// active represents the tags that will actually be visible from the list and rendered
				const active = children
					.filter(excludeNonTagsFromMeasurement)
					.slice(0, overflowedIndex === -1 ? children.length : overflowedIndex);

				// if there are hidden items left over, we need to account for the +N button
				// that will now appear and possibly require popping more tags off the list
				if (active.length < children.filter(excludeNonTagsFromMeasurement).length) {
					// Factor in the width of the see more button (which now appears in this calculation
					// since with hidden tabs we must show the +N) and the width of the clear all button
					totalWidth =
						active.reduce((sum, item) => {
							return sum + item.clientWidth + 8;
						}, 0) +
						SEE_MORE_BUTTON_WIDTH +
						clearAllButtonWidth;

					// remove active items until everything fits with the +N button and the See more button
					while (totalWidth >= parentWidth) {
						totalWidth = totalWidth - active[active.length - 1].clientWidth;
						active.pop();
					}

					// hidden represents the tags that are "contained" in the +N button and will be hidden from view
					// unless the user clicks on the +N button
					const hidden: number[] = [];
					children.forEach((child, index) => {
						if (!active[index] && excludeNonTagsFromMeasurement(child)) {
							hidden.push(index);
						}
					});
					setHiddenNavIndexes(hidden);
					return;
				}
				setHiddenNavIndexes([]);
			}
			setHiddenNavIndexes([]);
		}
	}, [clearAllButtonWidth]);

	const debouncedCalculateNav = useMemo(() => debounce(calculateNav, debounceTime), [calculateNav]);

	/* useEffect subscriptions below */

	useLayoutEffect(() => {
		// if we don't have the window.ResizeObserver rely on the generic window.resize to determine when to change the widths
		if (typeof ResizeObserver !== 'function') {
			window.addEventListener('resize', debouncedCalculateNav);
			return () => {
				window.removeEventListener('resize', debouncedCalculateNav);
			};
		}
	}, [debouncedCalculateNav]);

	// Recalculate if the size of the container where the tags are being displayed changes
	useEffect(() => {
		// if we have the window.ResizeObserver, use that to determine when we need to recalculate what is visible
		if (typeof ResizeObserver === 'function') {
			debouncedCalculateNav();
		}
	}, [observedWidth, debouncedCalculateNav]);

	// Recalculate if we get a new set of items
	useLayoutEffect(() => {
		calculateNav();
	}, [items, calculateNav]);

	const PlusOrShowLessButton = () => {
		return hiddenNavIndexes.length > 0 && items.length > 0 ? (
			<InputTag
				onClick={() => setShowAllMode(!showAllMode)}
				showCloseIcon={false}
				testId='show-more-less-tag'
			>
				<span
					data-testid='show-more-or-less-button'
					data-text={showAllMode ? 'Show less' : `+${hiddenNavIndexes.length}`}
				>
					{showAllMode ? 'Show less' : `+${hiddenNavIndexes.length}`}
				</span>
			</InputTag>
		) : null;
	};

	const ClearAllButton = () => {
		if (items.length && isEdittableInputingTagList) {
			return (
				<Button
					appearance='ghost'
					data-testid='clear-all-tags-button'
					id='clear-all-button'
					onClick={() => {
						if (removeAll) {
							removeAll();
						}
						setHiddenNavIndexes([]);
						setShowAllMode(false);
					}}
				>
					Clear all
				</Button>
			);
		}
		return null;
	};

	return (
		<div className={styles.mainTagWrapper} ref={mainTagWrapperRef}>
			<div
				className={classNames({
					[styles.tagContainer]: true,
					[styles.tagContainerWrap]: showAllMode,
				})}
				data-testid='input-tag-list'
			>
				{items.map(
					(item, index) =>
						(!hiddenNavIndexes.includes(index) || showAllMode) && (
							<InputTag
								key={`${item.label}-tag-list`}
								onClick={removeFunction ? () => removeFunction(item) : null}
								showCloseIcon={isEdittableInputingTagList}
								testId={`input-tag-${item.label}`}
							>
								{item.label}
							</InputTag>
						)
				)}

				<PlusOrShowLessButton />
				<ClearAllButton />
			</div>
			<div
				className={classNames({
					[styles.tagContainer]: true,
					[styles.measure]: true,
				})}
				ref={measuringContainerRef}
				style={{ width: mainTagWrapperRef?.current?.clientWidth || 0 }}
			>
				{items.map(item => (
					<InputTag key={`${item.label}-tag-container`} testId={`hidden-input-tag-${item.label}`}>
						{item.label}
					</InputTag>
				))}
			</div>
		</div>
	);
};
